import React, { useRef, useState, useCallback, useEffect } from 'react';
import styles from './index.module.scss';
import { useScreenSlideIn } from '@/hooks/useScreenSlideIn';
import imgPic1 from '@/assets/home/new-screen8-1-pic.png';
import imgPic2 from '@/assets/home/new-screen8-2-pic.png';
import imgPic3 from '@/assets/home/new-screen8-3-pic.png';
import imgPic4 from '@/assets/home/new-screen8-4-pic.png';
import imgPic5 from '@/assets/home/new-screen8-5-pic.png';
import imgPic6 from '@/assets/home/new-screen8-6-pic.png';
import imgPic7 from '@/assets/home/new-screen8-7-pic.png';
import imgPic8 from '@/assets/home/new-screen8-8-pic.png';
// import CircleArrow from './CircleArrow';
import CircleArrowButton from '@/components/CircleArrowButton';

interface CarouselItem {
  id: number;
  num: string;
  titleLines: [string, string];
  subtitle: string;
  pic: string;
}

const carouselItems: CarouselItem[] = [
  {
    id: 0,
    num: '01',
    titleLines: ['"人工智能+"', '创新应用实践案例'],
    subtitle: '发放机构：新华网联合工业与信息化部软件与集成电路促进中心',
    pic: imgPic1,
  },
  {
    id: 1,
    num: '02',
    titleLines: ['2026', '年度中国独角兽企业'],
    subtitle: '发放机构：中关村独角兽企业发展联盟',
    pic: imgPic2,
  },
  {
    id: 2,
    num: '03',
    titleLines: ['2025', '高新技术企业证书'],
    subtitle: '北京市科学技术委员会&北京市财政局&国家税务总局北京市税务局',
    pic: imgPic3,
  },
  {
    id: 3,
    num: '04',
    titleLines: ['北京市', '“专精特新”中小企业'],
    subtitle: '发放机构：北京市经济和信息化局',
    pic: imgPic4,
  },
  {
    id: 4,
    num: '05',
    titleLines: ['北京市', '“创新型”中小企业'],
    subtitle: '发放机构：北京市经济和信息化局',
    pic: imgPic5,
  },
  {
    id: 5,
    num: '06',
    titleLines: ['教育', 'AI先锋企业奖'],
    subtitle: '发放机构：2025GET教育科技大会',
    pic: imgPic6,
  },
  {
    id: 6,
    num: '07',
    titleLines: ['2025', '人工智能创新创业大赛三等奖'],
    subtitle: '发放机构：上地UPSIDE',
    pic: imgPic7,
  },
  {
    id: 7,
    num: '08',
    titleLines: ['2026', '匠心领学官'],
    subtitle: '发放机构：2026巨量引擎教育FUTURE知识生态大会',
    pic: imgPic8,
  },
];

const TRACK_SLOTS = 10; // [last, 0, 1, 2, 3, 0] for infinite
const SLOT_WIDTH_PERCENT = 100 / TRACK_SLOTS;

// 走马灯 track 顺序：[card3, card0, card1, card2, card3, card0]，便于向前/向后无缝循环
const trackOrder = [
  carouselItems[7],
  carouselItems[0],
  carouselItems[1],
  carouselItems[2],
  carouselItems[3],
  carouselItems[4],
  carouselItems[5],
  carouselItems[6],
  carouselItems[7],
  carouselItems[0],
];

export default function Screen8() {
  const slideInRef = useScreenSlideIn({
    triggerSlideRatio: 0.8,
    animationClassName: styles['screen8-anim-start'],
  });
  const trackRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // slideIndex 1、2、3、4 对应展示第 0、1、2、3 张卡片；0、5 为克隆位，过渡后重置
  const [slideIndex, setSlideIndex] = useState(1);

  const goNext = useCallback(() => {
    if (slideIndex < TRACK_SLOTS - 1) setSlideIndex(slideIndex + 1);
  }, [slideIndex]);

  const goPrev = useCallback(() => {
    if (slideIndex > 0) setSlideIndex(slideIndex - 1);
  }, [slideIndex]);

  const handleTrackTransitionEnd = useCallback(
    (e: React.TransitionEvent) => {
      if (e.target !== trackRef.current || e.propertyName !== 'transform') return;
      const track = trackRef.current;
      if (!track) return;
      if (slideIndex === 0) {
        track.style.transition = 'none';
        setSlideIndex(TRACK_SLOTS - 2);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            track.style.transition = '';
          });
        });
      } else if (slideIndex === TRACK_SLOTS - 1) {
        track.style.transition = 'none';
        setSlideIndex(1);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            track.style.transition = '';
          });
        });
      }
    },
    [slideIndex],
  );

  const goNextRef = useRef(goNext);
  const goPrevRef = useRef(goPrev);
  goNextRef.current = goNext;
  goPrevRef.current = goPrev;

  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    let lastT = 0;
    let acc = 0;
    let lastTriggerAt = 0;
    const THRESHOLD = 80;
    const WINDOW = 400;
    const COOLDOWN_MS = 700;

    const handleWheel = (e: WheelEvent) => {
      const deltaX = e.deltaX;
      const deltaY = e.deltaY;
      const isHorizontalScroll =
        Math.abs(deltaX) > Math.abs(deltaY) ||
        (Math.abs(deltaX) > 0 && Math.abs(deltaY) < 3) ||
        (Math.abs(deltaX) > Math.abs(deltaY) * 0.3 && Math.abs(deltaY) < 10);

      if (isHorizontalScroll && !e.defaultPrevented) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (!isHorizontalScroll) return;

      const now = Date.now();
      const dt = lastT ? now - lastT : 0;
      if (dt > WINDOW) acc = 0;
      lastT = now;
      acc += deltaX;
      if (Math.abs(acc) < THRESHOLD) return;
      if (Date.now() - lastTriggerAt < COOLDOWN_MS) {
        acc = 0;
        return;
      }
      lastTriggerAt = Date.now();
      acc = 0;
      if (deltaX > 0) goNextRef.current();
      else goPrevRef.current();
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <section className={styles['screen8-cover']} ref={slideInRef.ref}>
      <h2 className={styles['screen8-title']}>权威认证 品质保证</h2>

      <div className={styles['screen8-carousel-wrapper']} ref={carouselRef}>
        <CircleArrowButton
          direction="left"
          onClick={goPrev}
          className={styles['screen8-button-left']}
        />
        <div className={styles['screen8-carousel']}>
          <div
            ref={trackRef}
            className={styles['screen8-carousel-track']}
            style={{
              transform: `translateX(-${slideIndex * SLOT_WIDTH_PERCENT}%)`,
            }}
            onTransitionEnd={handleTrackTransitionEnd}
          >
            {trackOrder.map((item, index) => (
              <div key={`${item.id}-${index}`} className={styles['screen8-carousel-item']}>
                <div
                  className={`${styles['screen8-card']} ${styles[`screen8-card-${item.id + 1}`]}`}
                >
                  <img
                    src={item.pic}
                    alt=""
                    className={`${styles['screen8-card-pic']} ${styles[`screen8-card-pic-${item.id + 1}`]}`}
                    loading="lazy"
                  />
                  <div className={styles['screen8-card-content']}>
                    <h3 className={styles['screen8-card-title']}>
                      {item.titleLines[0]} <br />
                      {item.titleLines[1]}
                    </h3>
                    <p className={styles['screen8-card-subtitle']}>{item.subtitle}</p>
                    <span
                      className={`${styles['screen8-card-num']} ${
                        styles[`screen8-card-num-${item.id + 1}`]
                      }`}
                      aria-hidden
                    >
                      {item.num}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <CircleArrowButton
          direction="right"
          onClick={goNext}
          className={styles['screen8-button-right']}
        />
      </div>

      <div className={styles['screen8-steps']}>
        {carouselItems.map((item) => (
          <div
            key={item.id}
            className={[
              styles['screen8-step-item'],
              slideIndex % (TRACK_SLOTS - 2) === (item.id + 1) % (TRACK_SLOTS - 2)
                ? styles['screen8-step-item-active']
                : '',
            ].join(' ')}
            onClick={() => setSlideIndex(item.id + 1)}
          />
        ))}
      </div>
    </section>
  );
}
